import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  initGame, move, addRandomTile, checkGameOver, checkWon,
  getTileColor, getTileTextColor, getTileFontSize, saveBest, loadBest,
} from './gameLogic';
import './Game2048.css';

function Game2048() {
  const navigate = useNavigate();
  const [state, setState] = useState(initGame);
  const [touchStart, setTouchStart] = useState(null);
  const gameRef = useRef(null);

  const handleMove = useCallback((direction) => {
    setState((prev) => {
      if (prev.gameOver) return prev;

      const { grid: newGrid, scoreGained, moved } = move(prev.grid, direction);
      if (!moved) return prev;

      const withNew = addRandomTile(newGrid);
      const newScore = prev.score + scoreGained;
      const best = Math.max(newScore, prev.bestScore);
      saveBest(best);

      const won = !prev.won && checkWon(withNew);
      const gameOver = checkGameOver(withNew);

      return {
        grid: withNew,
        score: newScore,
        bestScore: best,
        gameOver,
        won: prev.won || won,
        history: [...prev.history.slice(-9), { grid: prev.grid, score: prev.score }],
      };
    });
  }, []);

  const handleUndo = () => {
    setState((prev) => {
      if (prev.history.length === 0) return prev;
      const last = prev.history[prev.history.length - 1];
      return {
        ...prev,
        grid: last.grid,
        score: last.score,
        gameOver: false,
        history: prev.history.slice(0, -1),
      };
    });
  };

  const handleNewGame = () => {
    setState({ ...initGame(), bestScore: loadBest() });
  };

  useEffect(() => {
    const handleKey = (e) => {
      switch (e.key) {
        case 'ArrowLeft': e.preventDefault(); handleMove('left'); break;
        case 'ArrowRight': e.preventDefault(); handleMove('right'); break;
        case 'ArrowUp': e.preventDefault(); handleMove('up'); break;
        case 'ArrowDown': e.preventDefault(); handleMove('down'); break;
        default: break;
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [handleMove]);

  const handleTouchStart = (e) => {
    setTouchStart({ x: e.touches[0].clientX, y: e.touches[0].clientY });
  };

  const handleTouchEnd = (e) => {
    if (!touchStart) return;
    const dx = e.changedTouches[0].clientX - touchStart.x;
    const dy = e.changedTouches[0].clientY - touchStart.y;
    const absDx = Math.abs(dx);
    const absDy = Math.abs(dy);

    if (Math.max(absDx, absDy) < 30) return;

    if (absDx > absDy) {
      handleMove(dx > 0 ? 'right' : 'left');
    } else {
      handleMove(dy > 0 ? 'down' : 'up');
    }
    setTouchStart(null);
  };

  const maxTile = Math.max(...state.grid.flat());

  return (
    <div className="game-app">
      <div className="game-topbar">
        <div className="game-topbar-left">
          <button className="game-back-btn" onClick={() => navigate('/')}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="game-title">2048</h1>
          <span className="game-max-tile" style={{ background: getTileColor(maxTile), color: getTileTextColor(maxTile) }}>
            {maxTile}
          </span>
        </div>
        <div className="game-topbar-right">
          <div className="game-scores">
            <div className="game-score-box">
              <span className="game-score-label">Score</span>
              <span className="game-score-value">{state.score.toLocaleString()}</span>
            </div>
            <div className="game-score-box">
              <span className="game-score-label">Best</span>
              <span className="game-score-value">{state.bestScore.toLocaleString()}</span>
            </div>
          </div>
          <button className="game-action-btn" onClick={handleUndo} disabled={state.history.length === 0} title="Undo">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="1 4 1 10 7 10" />
              <path d="M3.51 15a9 9 0 105.17-9.34L1 10" />
            </svg>
          </button>
          <button className="game-action-btn new" onClick={handleNewGame} title="New Game">
            New Game
          </button>
        </div>
      </div>

      <div className="game-container">
        <div className="game-instructions">
          <span>Use <strong>arrow keys</strong> or <strong>swipe</strong> to move tiles. Merge same numbers to reach <strong>2048!</strong></span>
        </div>

        <div
          className="game-board"
          ref={gameRef}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          {/* Background cells */}
          <div className="game-bg-grid">
            {Array.from({ length: 16 }).map((_, i) => (
              <div key={i} className="game-bg-cell"></div>
            ))}
          </div>

          {/* Tiles */}
          <div className="game-tiles">
            {state.grid.map((row, r) =>
              row.map((value, c) =>
                value !== 0 ? (
                  <div
                    key={`${r}-${c}`}
                    className={`game-tile ${value >= 2048 ? 'super' : ''}`}
                    style={{
                      '--r': r,
                      '--c': c,
                      background: getTileColor(value),
                      color: getTileTextColor(value),
                      fontSize: getTileFontSize(value),
                    }}
                  >
                    {value}
                  </div>
                ) : null
              )
            )}
          </div>

          {/* Game Over Overlay */}
          {state.gameOver && (
            <div className="game-overlay">
              <div className="game-overlay-content">
                <h2>Game Over!</h2>
                <p>Final Score: <strong>{state.score.toLocaleString()}</strong></p>
                <div className="game-overlay-btns">
                  <button className="game-overlay-btn" onClick={handleNewGame}>Try Again</button>
                  <button className="game-overlay-btn secondary" onClick={handleUndo}>Undo</button>
                </div>
              </div>
            </div>
          )}

          {/* Won Overlay (shows once) */}
          {state.won && !state.gameOver && state.grid.flat().includes(2048) && state.history.length > 0 && state.history[state.history.length - 1].grid.flat().every(v => v !== 2048) && (
            <div className="game-overlay won">
              <div className="game-overlay-content">
                <h2>You Win! 🎉</h2>
                <p>You reached 2048!</p>
                <button className="game-overlay-btn" onClick={() => setState(s => ({ ...s, won: true }))}>
                  Keep Playing
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Stats */}
        <div className="game-stats">
          <div className="game-stat-item">
            <span className="game-stat-label">Moves</span>
            <span className="game-stat-value">{state.history.length}</span>
          </div>
          <div className="game-stat-item">
            <span className="game-stat-label">Max Tile</span>
            <span className="game-stat-value">{maxTile}</span>
          </div>
          <div className="game-stat-item">
            <span className="game-stat-label">Empty</span>
            <span className="game-stat-value">{state.grid.flat().filter(v => v === 0).length}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Game2048;
